<template>
  <BaseButtonDropdown
    ref="dropdownRef"
    v-model="visible"
    v-intersection="intersectionConfig"
    :aria-label="dropdownAriaLabel"
    close-on-click
    color="ds-denim-9"
    flat
    flip
    :icon="dropDown.icon"
    :icon-size="dropDown.size"
    open-dropdown-button-testid="action-sheet-trigger"
    padding="xs"
    round
    :teleport="menuTeleportTarget"
    @hide="onVisibilityChange(false)"
    @show="onVisibilityChange(true)"
  >
    <div
      ref="menuRef"
      class="column dropdown-list"
      role="menu"
      @click.stop
      @keydown="onMenuKeydown"
      @keydown.esc.stop
    >
      <!-- This action is only available for online/print non english content -->
      <BaseButton
        v-if="canTranslateItem"
        v-bind="buttonAttrs"
        data-testid="translateItem"
        @click="handleTranslateItem"
      >
        <BaseTranslationDescription class="q-px-md full-width">
          {{ $t("actionsheet.translate_to_english") }}
        </BaseTranslationDescription>
      </BaseButton>
      <BaseSeparator />

      <div
        v-if="canOpenInternalLink"
        class="column relative"
      >
        <BaseButton
          v-bind="buttonAttrs"
          class="flex-1"
          data-testid="openInternalLink"
          icon="view-full-media-item"
          :label="openInternalLinkLabel"
          @click="openInternalLink"
        />
        <div class="copy-to-clipboard">
          <BaseButton
            v-if="!archivedContentContext"
            :aria-label="$t('global.copy_to_clipboard')"
            circle
            flat
            icon="copy"
            icon-size="md"
            new-design
            new-icon
            size="sm"
            @click="copyLinkToClipboard"
          />
          <BaseStreemTooltip>{{
            $t("global.copy_to_clipboard")
          }}</BaseStreemTooltip>
        </div>
      </div>

      <!--This action is only available for external items -->
      <BaseButton
        v-if="canEditExternalItem"
        v-bind="buttonAttrs"
        data-testid="editExternalItem"
        icon="edit"
        :label="$t('actionsheet.edit_external_item')"
        @click="openEditExternalItemForm"
      />
      <!--This action is always displayed on action sheet-->
      <BaseButton
        v-if="canPushToTeam"
        data-testid="pushToTeam"
        v-bind="buttonAttrs"
        icon="push-to-team"
        :label="$t('actionsheet.push_to_team')"
        @click="openPushToTeamModal"
      />
      <!--Action not available for social mentions-->
      <BaseButton
        v-if="canEmailOrShare"
        class="relative"
        :class="{ teaser: hasShareMentionTeaser }"
        data-testid="share"
        v-bind="buttonAttrs"
        icon="share"
        :label="$t('actionsheet.share')"
        @click="handleShareMentionClick"
      >
        <span class="relative">
          {{ $t("actionsheet.share") }}
          <TeaserIndicator
            v-if="
              showIndicator('share_online_broadcast') && hasShareMentionTeaser
            "
          />
        </span>
      </BaseButton>
      <BaseButton
        v-if="canDeleteMediaRequest"
        v-bind="buttonAttrs"
        data-testid="deleteMediaRequest"
        icon="delete"
        :label="$t('global.delete')"
        @click="deleteMediaRequest"
      />
      <!--Action only available for media items (caption or radio clip)-->
      <BaseButton
        v-if="canRequestMediaItem"
        v-bind="buttonAttrs"
        data-testid="requestMediaItem"
        icon="request-media-item"
        :label="$t('actionsheet.request_media_item')"
        @click="openMediaItemRequest()"
      />
      <BaseButton
        v-if="canInstantEditMediaItem"
        v-bind="buttonAttrs"
        class="relative"
        :class="{ teaser: hasCreateInstantEditMediaItemTeaser }"
        data-testid="instantEditMediaItem"
        icon="instant-edit-download"
        :label="$t('actionsheet.instant_edit_media_item')"
        @click="handleInstantEditMediaItemClick"
      >
        <span class="relative">
          {{ $t("actionsheet.instant_edit_media_item") }}
          <TeaserIndicator
            v-if="
              showIndicator('has_instant_edit_media_item') &&
              hasCreateInstantEditMediaItemTeaser
            "
          />
        </span>
      </BaseButton>
      <!--Action only available for transcript request items (caption or radio clip or podcast)-->
      <BaseButton
        v-if="canRequestTranscriptItem"
        v-bind="buttonAttrs"
        data-testid="requestTranscript"
        icon="request-transcript"
        :label="$t('actionsheet.request_transcript')"
        @click="openTranscriptRequest()"
      />
      <!--Action only available for transcript request items (caption or radio clip)-->
      <BaseButton
        v-if="canInstantEditTranscriptItem"
        v-bind="buttonAttrs"
        class="relative"
        :class="{ teaser: hasCreateInstantEditTranscriptRequestTeaser }"
        data-testid="instantEditTranscriptItem"
        icon="instant-edit-download"
        :label="$t('actionsheet.instant_edit_transcript')"
        @click="handleInstantEditTranscriptRequestClick"
      >
        <span class="relative">
          {{ $t("actionsheet.instant_edit_transcript") }}
          <TeaserIndicator
            v-if="
              showIndicator('has_instant_edit_transcript') &&
              hasCreateInstantEditTranscriptRequestTeaser
            "
          />
        </span>
      </BaseButton>
      <!--Action only available for articles-->
      <BaseButton
        v-if="canViewSimilarMentions"
        v-bind="buttonAttrs"
        data-testid="viewSimilarMentions"
        icon="view-similar-mentions"
        :label="$t('actionsheet.view_similar_mentions')"
        @click="openSimilarMentions"
      />
      <!--Action available for all mention types except tweets-->
      <BaseButton
        v-if="canAddToOrganisationReport"
        v-bind="buttonAttrs"
        data-testid="addToCuratedEmailReport"
        icon="add-to-cer"
        :label="$t('actionsheet.curated_email_report')"
        @click="openAddToOrganisationReportModal"
      />

      <!--This action is display if not on bookmarks page-->
      <BookmarkStreamActions
        v-if="hasAction('tag-media-item')"
        :is-social="isSocial"
        :mention="mention"
        :source-stream="stream"
        @tags-changed="reloadTagsOnMentionCard"
      />

      <!--Action only available if we have a valid stream-->
      <div class="column">
        <BaseButton
          v-if="canExcludeMention"
          v-bind="buttonAttrs"
          data-testid="excludeMention"
          icon="exclude-mention"
          :label="$t('actionsheet.remove_from', { streamLabel })"
          @click="openExcludeMentionModal"
        />
        <BaseStreemTooltip>
          <i18n-t
            keypath="actionsheet.tooltips.exclude_mention"
            scope="global"
          >
            <template #streamLabel>
              {{ streamLabel }}
            </template>

            <template #newline>
              <br />
            </template>

            <template #editIcon>
              <QIcon
                name="ion-md-settings"
                size="small"
              />
            </template>
          </i18n-t>
        </BaseStreemTooltip>
      </div>
      <!--Action only available for a mention which is in a folder currently viewed-->
      <BaseButton
        v-if="canRemoveFromTaggedFolder"
        data-testid="removeFromTagged"
        v-bind="buttonAttrs"
        icon="untag"
        :label="$t('actionsheet.remove_from_folder')"
        @click="confirmRemoveFromBookmarkStream"
      />
      <BaseButton
        v-if="canReplaceArchivedClip"
        data-testid="replaceArchivedClip"
        v-bind="buttonAttrs"
        icon="reload"
        :label="$t('actionsheet.replace_archived_clip')"
        @click="replaceArchivedClip"
      />
      <BaseButton
        v-if="canRevertToOriginalClip"
        data-testid="revertClip"
        v-bind="buttonAttrs"
        icon="close"
        :label="$t('actionsheet.revert_to_original_clip')"
        @click="openRevertToOriginalClipConfirmationModal"
      />

      <!--Action only available if we have a valid stream-->
      <BaseSeparator />
      <BaseButton
        v-if="canShowDebugInfo"
        data-testid="debug"
        v-bind="buttonAttrs"
        icon="debug"
        :label="$t('actionsheet.show_debug_info')"
        @click="openDebugMentionModal"
      />
      <BaseButton
        v-if="canShowSocialMentionDetail"
        data-testid="open-mention-detail-modal"
        v-bind="buttonAttrs"
        icon="view-mention"
        :label="$t('actionsheet.open_mention_detail_modal')"
        @click="navigateToMentionDetail"
      />
      <BaseButton
        v-if="canShowAdminModeEdit"
        data-testid="editInCrm"
        v-bind="buttonAttrs"
        icon="edit"
        :label="$t('actionsheet.edit_in_crm')"
        @click="editMentionInAdminMode"
      />
      <BaseButton
        v-if="canFlagForAmReview"
        data-testid="flagForReview"
        v-bind="buttonAttrs"
        icon="flag"
        :label="$t('actionsheet.flag_for_review')"
        @click="openFlagForReviewModal"
      />
    </div>
  </BaseButtonDropdown>
</template>

<script>
import { storeToRefs } from "pinia";
import { Notify } from "quasar";
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseButtonDropdown,
  BaseSeparator,
  BaseStreemTooltip,
} from "shared/components/base";
import BookmarkStreamActions from "shared/components/BookmarkStreamActions/BookmarkStreamActions.vue";
import { useModalFocusTrapTarget } from "shared/components/core/modals/useModalFocusTrapTarget";
import TeaserIndicator from "shared/components/core/various/TeaserIndicator.vue";
import BaseTranslationDescription from "shared/components/translation/BaseTranslationDescription.vue";
import useClipboard from "shared/composables/useClipboard";
import useContentWarning from "shared/composables/useContentWarning";
import useFeatureTeasers from "shared/composables/useFeatureTeasers";
import useMediaItemRequest from "shared/composables/useMediaItemRequest";
import useMention from "shared/composables/useMention";
import { useTranslation } from "shared/composables/useTranslation";
import { streamTypes } from "shared/constants";
import { isBroadcastProviderUnsupported } from "shared/helpers/broadcastProvider";
import { isTveyesExpired } from "shared/helpers/date";
import { openExternalUrl } from "shared/helpers/external";
import { labelForStreamType } from "shared/helpers/stream";
import ModalService from "shared/modals/ModalService";
import { useSideBarStore } from "shared/modules/SideBar/stores/useSideBarStore";
import TranscriptRequestService from "shared/services/api/TranscriptRequestService";
import { isMediaRequestCompleted } from "shared/services/mediaRequest";
import {
  canReplaceArchivedClip,
  hasArchivedTvEyesContent,
  openViewMentionDetailWithKinetiqModal,
  openViewMentionDetailWithTvEyesModal,
} from "shared/services/mention";
import { removeMention } from "shared/services/stream";
import { useBookmarkStreamsStore } from "shared/stores/bookmarkStreams";
import { useStreamsStore } from "shared/stores/streams";
import { useUserStore } from "shared/stores/user";
import {
  isKinetiqMention,
  isSocialMentionType,
  isTvEyesMention,
} from "shared/types/mentions/guards";

import { useDesktopStore } from "src/stores/desktop";

export default {
  name: "ActionSheet",
  components: {
    BookmarkStreamActions,
    BaseButton,
    BaseButtonDropdown,
    BaseSeparator,
    TeaserIndicator,
    BaseTranslationDescription,
    BaseStreemTooltip,
  },
  inject: {
    podcastEpisodeDuration: {
      from: "podcastEpisodeDuration",
      default: 0,
    },
    groupSlug: {
      default: "",
    },
  },
  props: {
    actions: {
      type: Array,
      default: () => [],
    },
    origin: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    mention: {
      type: Object,
      required: true,
    },
    isMentionTranslated: {
      type: Boolean,
    },
    stream: {
      type: Object,
      default: () => ({}),
    },
    dropDown: {
      type: Object,
      required: false,
      default: () => ({
        icon: "more_vert",
        size: "sm",
      }),
    },
    buttonColor: {
      type: String,
      required: false,
      default: "inherit",
    },
    isFrontPage: {
      type: Boolean,
    },
    mediaRequest: {
      type: Object,
      required: false,
      default: () => null,
    },
  },
  emits: [
    "click",
    "delete-media-request",
    "mention-removed",
    "tags-changed-in-action-sheet",
    "translate",
    "close",
  ],
  setup(mentionProps, context) {
    const modalFocusTrapTarget = useModalFocusTrapTarget();

    const menuTeleportTarget = computed(
      () => modalFocusTrapTarget?.value ?? "body"
    );

    const { copyToClipboard } = useClipboard();

    const { createViewedItemForFeature, viewedFeatureTeaser } =
      useFeatureTeasers();

    const { openShareMentionContentWarningModal } = useContentWarning();

    const { mentionClicked } = useMention(mentionProps, context);

    const { canTranslateMention } = useTranslation(mentionProps.mention);

    const bookmarkStreamsStore = useBookmarkStreamsStore();

    const { removeFromBookmarkStream } = bookmarkStreamsStore;

    const streamsStore = useStreamsStore();
    const { bookmarkStreams } = storeToRefs(streamsStore);

    const sideBarStore = useSideBarStore();
    const { showSideBar, setSideBarContent } = sideBarStore;

    const userStore = useUserStore();

    const { hasAdvancedAccess, isAdminUser, customerViewEnabled } =
      storeToRefs(userStore);

    const desktopStore = useDesktopStore();
    const { organisationReportSpec } = desktopStore;

    const { openMediaItemRequestModalWithDuplicateCheck } =
      useMediaItemRequest();

    return {
      menuTeleportTarget,
      bookmarkStreams,
      copyToClipboard,
      createViewedItemForFeature,
      viewedFeatureTeaser,
      openShareMentionContentWarningModal,
      mentionClicked,
      canTranslateMention,
      removeFromBookmarkStream,

      showSideBar,
      setSideBarContent,

      hasAdvancedAccess,
      isAdminUser,
      customerViewEnabled,

      organisationReportSpec,
      openMediaItemRequestModalWithDuplicateCheck,
    };
  },
  data() {
    return {
      visible: false,
      duplicateTranscriptRequest: false,
      buttonAttrs: {
        newDesign: true,
        newIcon: true,
        actionItem: true,
        iconSize: "lg",
      },
    };
  },
  computed: {
    dropdownAriaLabel() {
      const mentionTitle = this.mention?.title;

      if (mentionTitle) {
        return getLocaleText("actionsheet.show_menu_for", {
          item: mentionTitle,
        });
      }

      return getLocaleText("actionsheet.show_menu");
    },
    canOpenInternalLink() {
      return (
        this.hasAction("view-internal-link") &&
        this.mention.type !== "external_item" &&
        !this.isSocial &&
        !this.mention.outside_source_retention_period &&
        (!this.archivedContentContext || !this.isPlaybackUnsupported)
      );
    },
    openInternalLinkLabel() {
      const keySuffix = this.archivedContentContext
        ? "view_archived_media_item"
        : "view_full_media_item";

      return getLocaleText(`actionsheet.${keySuffix}`);
    },
    streamLabel() {
      return labelForStreamType(this.stream?.type);
    },
    isSocial() {
      return isSocialMentionType(this.mention.type);
    },
    isBrief() {
      return this.stream.locked;
    },
    archivedContentContext() {
      return Boolean(this.mediaRequest);
    },
    canRemoveFromTaggedFolder() {
      if (!this.hasAction("remove-from-tagged-folder")) {
        return false;
      }

      return this.bookmarkStreams.find(
        (bookmarkStream) => bookmarkStream.id === this.stream.id
      );
    },
    canReplaceArchivedClip() {
      if (!this.hasAction("replace-archived-clip")) {
        return false;
      }

      return canReplaceArchivedClip(this.mention);
    },
    canRevertToOriginalClip() {
      if (!this.hasAction("revert-to-original-clip")) {
        return false;
      }

      if (this.isPlaybackUnsupported) return false;

      const tvEyesMediaRequest = this.mention.tveyes_media_request;

      return tvEyesMediaRequest && isMediaRequestCompleted(tvEyesMediaRequest);
    },
    canShareMention() {
      if (this.isSocial) return false;
      if (this.isPlaybackUnsupported) return false;
      if (this.mention.outside_source_retention_period) return false;

      if (!this.$permissions.has("can_share_or_email_content_externally")) {
        return false;
      }

      if (this.isBroadcastType) {
        return (
          this.$features.has("share_online_broadcast") || this.hasAdvancedAccess
        );
      }

      return true;
    },
    canRequestMediaItem() {
      return (
        this.hasAction("request-media-item") &&
        this.isBroadcastType &&
        this.$features.has("media_request") &&
        this.$permissions.has("can_request_media_items") &&
        !this.isPlaybackUnsupported &&
        !this.isTvEyes &&
        !this.isKinetiq
      );
    },
    canInstantEditMediaItem() {
      return (
        this.hasAction("instant-edit-media-item") &&
        !this.isPlaybackUnsupported &&
        !this.isKinetiq &&
        (this.canCreateInstantEditMediaItem ||
          this.hasCreateInstantEditMediaItemTeaser)
      );
    },
    canCreateInstantEditMediaItem() {
      return (
        this.$features.has("has_instant_edit_media_item") &&
        this.$permissions.has("can_create_instant_edit_media_items") &&
        this.isBroadcastType &&
        this.$isDesktop &&
        !this.isPlaybackUnsupported &&
        !this.isTvEyes &&
        !this.isKinetiq
      );
    },
    canRequestTranscriptItem() {
      return (
        this.hasAction("request-transcript-item") &&
        this.isMediaPlayerType &&
        this.$features.has("audio_transcript") &&
        this.$permissions.has("can_request_transcripts") &&
        !this.isPlaybackUnsupported &&
        !this.isTvEyes &&
        !this.isKinetiq
      );
    },
    canInstantEditTranscriptItem() {
      return (
        this.hasAction("instant-edit-transcript-item") &&
        !this.isPlaybackUnsupported &&
        !this.isKinetiq &&
        (this.canCreateInstantEditTranscriptRequest ||
          this.hasCreateInstantEditTranscriptRequestTeaser)
      );
    },
    canCreateInstantEditTranscriptRequest() {
      return (
        this.$features.has("has_instant_edit_transcript") &&
        this.$permissions.has("can_create_instant_edit_transcripts") &&
        this.isBroadcastType &&
        this.$isDesktop &&
        !this.isPlaybackUnsupported &&
        !this.isTvEyes &&
        !this.isKinetiq
      );
    },
    canAddToOrganisationReport() {
      return (
        this.hasAction("add-to-organisation-report") &&
        this.$features.has("has_reports") &&
        this.$permissions.has("can_add_any_mention_to_organisation_reports")
      );
    },
    canViewSimilarMentions() {
      return (
        this.hasAction("view-similar-mentions") &&
        this.$features.has("has_similar_mentions") &&
        this.stream.type !== streamTypes.socialStream &&
        this.mention.type !== "external_item"
      );
    },
    canFlagForAmReview() {
      return (
        this.hasAction("flag-for-am-review") &&
        this.isAdminUser &&
        this.hasAdvancedAccess &&
        this.organisationReportSpec &&
        !this.customerViewEnabled
      );
    },
    canShowDebugInfo() {
      return (
        this.hasAction("show-debug-info") &&
        this.hasAdvancedAccess &&
        !this.customerViewEnabled
      );
    },
    canShowSocialMentionDetail() {
      return this.isSocial && this.canShowDebugInfo && this.mention.media_id;
    },
    canShowAdminModeEdit() {
      return (
        this.hasAction("show-admin-mode-edit") &&
        this.isBroadcastType &&
        !this.isKinetiq &&
        this.hasAdvancedAccess &&
        !this.customerViewEnabled
      );
    },
    canEditExternalItem() {
      return (
        this.hasAction("edit-external-item") &&
        this.mention.type === "external_item"
      );
    },
    canPushToTeam() {
      return (
        this.hasAction("push-to-team") &&
        this.$features.has("has_push_to_team") &&
        !this.isPlaybackUnsupported
      );
    },
    canEmailOrShare() {
      return (
        this.hasAction("email-or-share") &&
        !this.isPlaybackUnsupported &&
        (this.canShareMention || this.hasShareMentionTeaser)
      );
    },
    canDeleteMediaRequest() {
      return (
        this.hasAction("delete-media-request") && this.archivedContentContext
      );
    },
    canExcludeMention() {
      return (
        this.hasAction("exclude-mention") &&
        this.stream &&
        this.stream.id &&
        this.$permissions.has("can_exclude_mentions") &&
        this.mention.type !== "external_item" &&
        ((this.stream.can_modify &&
          [streamTypes.mentionStream, streamTypes.socialStream].includes(
            this.stream.type
          )) ||
          [streamTypes.organisationBrief, streamTypes.reportingStream].includes(
            this.stream.type
          ))
      );
    },
    canTranslateItem() {
      return (
        this.hasAction("translate-item") &&
        !this.isMentionTranslated &&
        this.canTranslateMention
      );
    },
    isBroadcastType() {
      const types = [
        "radio_clip",
        "tv_caption",
        "tv_logo_appearance",
        "tv_super",
      ];

      return types.includes(this.mention.type);
    },
    isMediaPlayerType() {
      const types = [
        "tv_caption",
        "tv_super",
        "tv_logo_appearance",
        "radio_clip",
        "podcast_episode",
      ];

      return types.includes(this.mention.type);
    },
    isPlaybackUnsupported() {
      return isBroadcastProviderUnsupported(this.mention, this.mediaRequest);
    },
    isKinetiq() {
      return isKinetiqMention(this.mention);
    },
    isTvEyes() {
      return isTvEyesMention(this.mention);
    },
    intersectionConfig() {
      return this.visible ? { handler: this.onIntersection } : false;
    },
    hasShareMentionTeaser() {
      if (this.isSocial) return false;
      if (!this.$features.has("has_feature_teasers")) return false;

      if (this.isBroadcastType) {
        return (
          !this.$features.has("share_online_broadcast") &&
          this.$permissions.has("can_share_or_email_content_externally") &&
          !this.hasAdvancedAccess
        );
      }

      return false;
    },
    hasCreateInstantEditMediaItemTeaser() {
      return (
        this.$features.has("has_feature_teasers") &&
        !this.$features.has("has_instant_edit_media_item") &&
        this.$permissions.has("can_create_instant_edit_media_items") &&
        this.isBroadcastType
      );
    },
    hasCreateInstantEditTranscriptRequestTeaser() {
      return (
        this.$features.has("has_feature_teasers") &&
        !this.$features.has("has_instant_edit_transcript") &&
        this.$permissions.has("can_create_instant_edit_transcripts") &&
        this.isBroadcastType
      );
    },
  },
  methods: {
    hasAction(action) {
      return this.actions.length === 0 || this.actions.includes(action);
    },
    onIntersection({ isIntersecting }) {
      if (!isIntersecting && this.visible) {
        this.closeDropdown();
      }
    },
    onVisibilityChange(visible) {
      const { mention, stream } = this;
      const action = visible ? "Opened" : "Closed";

      this.$track(`${action} Action Sheet`, {
        id: mention.id,
        type: mention.type,
        streamId: stream.id,
      });

      this.visible = visible;

      if (visible) {
        this.$nextTick(() => this.focusMenuItem(0));
      }
    },
    closeDropdown() {
      this.visible = false;
    },
    getMenuItems() {
      const root = this.$refs.menuRef;
      if (!root) return [];

      return Array.from(root.querySelectorAll("button:not([disabled])"));
    },
    focusTrigger() {
      const trigger = this.$refs.dropdownRef?.$el?.querySelector(
        'button[aria-haspopup="menu"]'
      );

      trigger?.focus();
    },
    focusMenuItem(index) {
      const items = this.getMenuItems();
      if (!items.length) return;

      const wrapped = (index + items.length) % items.length;
      items[wrapped].focus();
    },
    onMenuKeydown(event) {
      const items = this.getMenuItems();
      if (!items.length) return;

      const currentIndex = items.indexOf(document.activeElement);

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.focusMenuItem(currentIndex + 1);

          return;
        case "ArrowUp":
          event.preventDefault();
          this.focusMenuItem(currentIndex - 1);

          return;
        case "Home":
          event.preventDefault();
          this.focusMenuItem(0);

          return;
        case "End":
          event.preventDefault();
          this.focusMenuItem(items.length - 1);

          return;
        case "Escape":
          event.preventDefault();
          this.closeDropdown();
          this.$nextTick(() => this.focusTrigger());
          break;
        default:
          break;
      }
    },
    findMatchingMentionBookmark(mention, bookmarkStreamId) {
      let matchingStreamBookmarkId;

      if (mention.bookmarks?.length) {
        const matchingStreamBookmark = mention.bookmarks.find(
          (bookmark) => bookmark.stream_id === bookmarkStreamId
        );

        matchingStreamBookmarkId = matchingStreamBookmark
          ? matchingStreamBookmark.id
          : undefined;
      }

      return matchingStreamBookmarkId;
    },
    // The method below is only used from the bookmark view because we don't alter
    // the mentions in the other workflows and the bookmarkId is not present in the
    // individual mention
    async confirmRemoveFromBookmarkStream() {
      const { stream: bookmarkStream, mention } = this;

      this.closeDropdown();

      const matchingStreamBookmarkId = this.findMatchingMentionBookmark(
        mention,
        bookmarkStream.id
      );

      await this.removeFromBookmarkStream({
        bookmarkStream,
        mention: { bookmark_id: matchingStreamBookmarkId },
      });

      this.$emit("mention-removed", this.mention);
    },
    replaceArchivedClip() {
      openViewMentionDetailWithTvEyesModal(this.mention, {
        stream: this.stream,
        forceReplaceArchivedClipView: true,
        trackTvEyesMediaRequestStatus: true,
      });
    },
    openRevertToOriginalClipConfirmationModal() {
      const isExpired = isTveyesExpired(this.mention);

      const msgKey = isExpired
        ? "actionsheet.revert_to_original_clip_modal.expired_content"
        : "actionsheet.revert_to_original_clip_modal.valid_content";

      ModalService.open("ConfirmDialogModal", {
        props: {
          type: "confirm",
          theme: "danger",
          title: getLocaleText("actionsheet.revert_to_original_clip"),
          dialogMaxWidth: "410px",
          message: getLocaleText(msgKey, {
            newline: "<br>",
          }),
          confirmLabel: getLocaleText("global.confirm"),
        },
        events: {
          confirm: () => {
            this.revertToOriginalClip();
            this.$emit("close");
          },
          close: () => ModalService.close("ConfirmDialogModal"),
        },
      });
    },
    async revertToOriginalClip() {
      try {
        const tvEyesMediaRequest = this.mention.tveyes_media_request;

        await this.$streemApiV1.put(
          `media_requests/${tvEyesMediaRequest.id}/cancel`
        );

        tvEyesMediaRequest.status = "Cancelled";

        Notify.create({
          message: getLocaleText("actionsheet.success.revert_to_original_clip"),
          color: "green",
          textColor: "white",
          timeout: 3000,
        });
      } catch (error) {
        this.$error(
          this.$t("actionsheet.errors.revert_to_original_clip"),
          error
        );
      }
    },
    async openMediaItemRequest(isInstantDownload = false) {
      this.closeDropdown();

      await this.openMediaItemRequestModalWithDuplicateCheck({
        mention: this.mention,
        stream: this.stream,
        type: "media_item",
        duplicateCheckMention: this.mention,
        origin: this.origin,
        shouldShowInstantEditButton: this.canCreateInstantEditMediaItem,
        isInstantDownload,
      });
    },
    async openTranscriptRequest(isInstantDownload = false) {
      this.closeDropdown();

      try {
        // eslint-disable-next-line prefer-destructuring
        this.duplicateTranscriptRequest = (
          await TranscriptRequestService.findDuplicates(this.mention, {
            seconds: 300,
          })
        )[0];
      } catch (error) {
        this.$error(
          this.$t("actionsheet.errors.duplicate_transcript_request"),
          error
        );
      } finally {
        if (this.duplicateTranscriptRequest) {
          this.openDuplicateTranscriptRequestModal(isInstantDownload);
        } else if (isInstantDownload) {
          this.openCreateInstantTranscriptRequestModal();
        } else {
          this.openRequestTranscriptItemModal();
        }
      }
    },
    openCreateInstantTranscriptRequestModal() {
      this.closeDropdown();
      const { mention, origin } = this;

      this.$track("Instant Edit Transcript Request modal opened", {
        id: mention.id,
        type: mention.type,
        streamId: this.stream.id,
      });

      this.$modals.open("CreateInstantDownloadModal", {
        props: {
          mention,
          origin,
          stream: this.stream,
          type: "transcript_request",
        },
      });
    },
    openEditExternalItemForm() {
      this.closeDropdown();
      const { mention } = this;

      this.$track("Clicked on Edit External Item", {
        id: mention.id,
        type: mention.type,
        streamId: this.stream.id,
      });

      this.$safeRouterPush({
        name: "external-items-edit",
        params: {
          externalItemId: mention.id,
          streamSlug: this.stream.slug,
          groupSlug: this.stream.group?.slug,
        },
      });
    },
    openInternalLink() {
      this.closeDropdown();

      if (isTvEyesMention(this.mention) && this.archivedContentContext) {
        openViewMentionDetailWithTvEyesModal(this.mention, {
          stream: this.stream,
          mediaRequest: this.mediaRequest,
          trackTvEyesMediaRequestStatus: true,
        });
      } else if (isKinetiqMention(this.mention) && this.mediaRequest) {
        openViewMentionDetailWithKinetiqModal(
          this.mention,
          this.mediaRequest,
          this.stream
        );
      } else {
        this.mentionClicked(this.mention, true);
      }
    },
    openSimilarMentions() {
      const { mention, stream } = this;

      this.$track("Opened Similar Mentions Sidebar", {
        id: mention.id,
        type: mention.type,
        streamId: stream.id,
      });

      this.setSideBarContent({
        title: this.$t("actionsheet.similar_mentions"),
        component: "SimilarMentions",
        data: {
          streamId: stream.id,
          mention,
        },
      });

      this.showSideBar();
      this.closeDropdown();
    },
    openShareMentionModal() {
      this.closeDropdown();

      this.$track("Opened Share Mention Modal", {
        id: this.mention.id,
        type: this.mention.type,
        streamId: this.stream.id,
      });

      this.$modals.open("ShareMentionModal", {
        props: {
          mention: this.mention,
          mediaRequest: this.mediaRequest,
        },
      });
    },
    deleteMediaRequest() {
      this.closeDropdown();
      this.$emit("delete-media-request");
    },
    async openPushToTeamModal() {
      this.closeDropdown();

      if (this.mention.requires_content_warning) {
        await this.openShareMentionContentWarningModal();
      }

      this.$track("Opened Push To Team Modal", {
        id: this.mention.id,
        type: this.mention.type,
        streamId: this.stream.id,
      });

      this.$modals.open("PushToTeamModal", {
        props: {
          mention: this.mention,
          mediaRequest: this.mediaRequest,
        },
      });
    },
    openDuplicateTranscriptRequestModal(isInstantDownload) {
      this.$track("Opened Duplicate Transcript Request Modal", {
        transcriptRequestItemId: this.duplicateTranscriptRequest.id,
      });

      const { mention, stream } = this;

      this.$modals.open("DuplicateMediaItemTranscriptRequestModal", {
        props: {
          mention,
          stream,
          type: "transcript_request",
          isInstantDownload,
          shouldShowInstantEditButton:
            this.canCreateInstantEditTranscriptRequest,
        },
      });
    },
    openRequestTranscriptItemModal() {
      this.closeDropdown();

      this.$track("Opened Request Transcript Modal", {
        id: this.mention.id,
        type: this.mention.type,
        streamId: this.stream.id,
      });

      this.$modals.open("RequestTranscriptItemModal", {
        props: {
          mention: this.mention,
          origin: this.origin,
          podcastEpisodeDuration: this.podcastEpisodeDuration,
        },
      });
    },
    openAddToOrganisationReportModal() {
      this.closeDropdown();

      this.$track("Opened Add to Organisation Report Modal", {
        id: this.mention.id,
        type: this.mention.type,
        streamId: this.stream.id,
      });

      this.$modals.open("AddToOrganisationReportModal", {
        props: {
          mentions: this.mention,
        },
      });
    },
    openFlagForReviewModal() {
      this.closeDropdown();

      this.$track("Opened Flag Mention For AM Review Modal", {
        id: this.mention.id,
        type: this.mention.type,
        streamId: this.stream.id,
      });

      this.$modals.open("FlagMentionForAMReview", {
        props: {
          mention: this.mention,
          stream: this.stream,
        },
        events: {
          close: () => {
            this.$modals.close("FlagMentionForAMReview");
          },
        },
      });
    },
    async removeMentions() {
      const { similarMentions } = this.mention;

      let mentions = [this.mention];

      if (similarMentions && similarMentions.length > 0) {
        mentions = mentions.concat(similarMentions);
      }

      await Promise.all(
        mentions.map((mention) => removeMention(this.stream, mention))
      );

      this.$emit("mention-removed", this.mention);
    },
    openExcludeMentionModal() {
      this.closeDropdown();

      this.$track(`Opened Remove from ${this.streamLabel} Modal`, {
        id: this.mention.id,
        type: this.mention.type,
        streamId: this.stream.id,
      });

      if (!this.isBrief) {
        this.$modals.open("ConfirmModal", {
          props: {
            message: this.$t("actionsheet.confirm_mention_exclusion", {
              streamLabel: this.streamLabel,
            }),
            messageClass: "q-pa-md",
          },
          events: {
            confirm: () => {
              this.removeMentions();
            },
          },
        });
      } else {
        const { similarMentions } = this.mention;

        let removedMentions = [this.mention];

        if (similarMentions && similarMentions.length > 0) {
          removedMentions = removedMentions.concat(similarMentions);
        }

        this.$modals.open("ExcludeMentionModal", {
          props: {
            removedMentions,
            stream: this.stream,
          },
          events: {
            removed: () => {
              this.$emit("mention-removed", this.mention);
            },
            close: () => {
              this.$modals.close("ExcludeMentionModal");
            },
          },
        });
      }
    },
    openDebugMentionModal() {
      this.closeDropdown();

      this.$modals.open("DebugMentionModal", {
        props: {
          mention: this.mention,
        },
      });
    },
    navigateToMentionDetail() {
      window.open(`/view/${this.mention.media_id}`, "_blank");
    },
    copyLinkToClipboard() {
      const url = new URL(this.mention.internal_link);

      if (this.hasArchivedTvEyesContent()) {
        url.searchParams.append("forceFullBroadcast", true);
      }

      this.copyToClipboard(url.toString());

      this.$track("Copied Mention Link to Clipboard", {
        id: this.mention.id,
        type: this.mention.type,
        streamId: this.stream.id,
      });
    },
    hasArchivedTvEyesContent() {
      return hasArchivedTvEyesContent(this.mention);
    },
    editMentionInAdminMode() {
      const type = this.mention.type === "radio_clip" ? "radio" : "tv";
      let url = "";
      const { original } = this.mention;

      if (
        type === "radio" &&
        original &&
        original.station_id &&
        original.start_time
      ) {
        url = `${
          import.meta.env.VITE_ADMIN_MODE_BASE_URL
        }${type}-player?sourceId=${original.station_id}&timestamp=${
          original.start_time
        }`;
      } else {
        url = `${
          import.meta.env.VITE_ADMIN_MODE_BASE_URL
        }${type}-player?sourceId=${this.mention.source.id}&timestamp=${
          this.mention.timestamp
        }`;
      }

      openExternalUrl(url);
    },
    reloadTagsOnMentionCard(bookmarks) {
      this.$emit("tags-changed-in-action-sheet", bookmarks);
    },
    showIndicator(feature) {
      return this.viewedFeatureTeaser(feature);
    },
    async openTeaserModal(feature) {
      this.closeDropdown();

      this.$modals.open("TeaserModal", {
        props: {
          feature,
        },
      });

      try {
        await this.createViewedItemForFeature(feature);
      } catch {
        // don't do anything
      }
    },
    async handleShareMentionClick() {
      this.closeDropdown();

      if (this.canShareMention) {
        if (this.mention.requires_content_warning) {
          await this.openShareMentionContentWarningModal();
        }

        this.openShareMentionModal();
      } else {
        this.openTeaserModal("share_online_broadcast");
      }
    },
    handleInstantEditMediaItemClick() {
      if (this.canCreateInstantEditMediaItem) {
        this.openMediaItemRequest(true);
      } else {
        this.openTeaserModal("has_instant_edit_media_item");
      }
    },
    handleInstantEditTranscriptRequestClick() {
      if (this.canCreateInstantEditTranscriptRequest) {
        this.openTranscriptRequest(true);
      } else {
        this.openTeaserModal("has_instant_edit_transcript");
      }
    },
    handleTranslateItem() {
      this.$emit("translate", this.mention);
      this.closeDropdown();
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown-list {
  max-height: 400px;
  overflow-y: auto;
  flex-wrap: nowrap;
}

:deep(button.base-button[aria-haspopup="menu"]:focus-visible) {
  outline: 2px solid var(--s-color-primary);
  outline-offset: 2px;
  border-radius: var(--s-border-radius-md);
}

.dropdown-list :deep(button.base-button__action-item:focus-visible) {
  outline: none;
  background: var(--c-button-bg-hover);
  border-color: var(--c-button-border-hover);
  color: var(--c-button-color-hover);
}

.copy-to-clipboard :deep(button:focus-visible) {
  outline: 2px solid var(--s-color-primary);
  outline-offset: 2px;
  border-radius: var(--s-border-radius-md);
}

.teaser {
  opacity: 0.6 !important;

  &:hover {
    opacity: 1 !important;
  }
}

.copy-to-clipboard {
  position: absolute;
  z-index: 10;
  right: 0.5em;
  top: 50%;
  margin-top: calc(var(--s-size-controls-sm) / -2);
}
</style>
